import { Conseiller, Dossier } from '@axa-fr-axion/user-journey-services';
import { Draft, produce } from 'immer';
import mergeWith from 'lodash.mergewith';
import { isAcceptedValidityStatus } from 'utils';
import { ExtendedFunctionalError, JourneyAction, JourneyData, JourneyReducer } from './App.interface';
import {
    EMPTY_JOURNEY_DATA,
    fromEstablishment,
    getNumberOfEmployeesFromEstablishment,
    getYearsOfExperienceFromEstablishment,
    selectDefaultAdvisor,
    toIdentity,
} from './App.utils';

export function handleInitialize(
    state: JourneyData,
    action: Extract<JourneyAction, { type: 'INITIALIZE' }>,
): JourneyData {
    return produce(state, (draft) => {
        draft.folder = action.folder as Draft<Dossier>;
        draft.identity = toIdentity(action.folder.personne);
    });
}

export function handleResetCompany(
    state: JourneyData,
    _action: Extract<JourneyAction, { type: 'RESET_COMPANY' }>,
): JourneyData {
    return produce(state, (draft) => {
        draft.company = EMPTY_JOURNEY_DATA.company;
        draft.isCompanyValid = false;
        draft.economicSector.profession = undefined;
        draft.economicSector.sector = undefined;
        draft.homeAddress.address = undefined;
        draft.homeAddress.city = undefined;
        draft.homeAddress.companyName = undefined;
        draft.homeAddress.postalCode = undefined;
    });
}

export function handleSelectAdvisor(
    state: JourneyData,
    action: Extract<JourneyAction, { type: 'SELECT_ADVISOR' }>,
): JourneyData {
    return produce(state, (draft) => {
        draft.selectedAdvisor = action.advisor as Draft<Conseiller>;
    });
}

export function handleRequestsErrors(
    state: JourneyData,
    action: Extract<JourneyAction, { type: 'REQUEST_ERROR' }>,
): JourneyData {
    return produce(state, (draft) => {
        draft.requestError = action.error as Draft<ExtendedFunctionalError>;
    });
}

export function handleSelectCompany(
    state: JourneyData,
    _action: Extract<JourneyAction, { type: 'SELECT_COMPANY' }>,
): JourneyData {
    return produce(state, (draft) => {
        const establishment = state.company?.establishment;

        if (establishment) {
            draft.economicSector = {
                sector: {
                    label: establishment.establishment_activity_label || '',
                    value: establishment.business_sector,
                },
            };
        }

        const numberOfEmployees = getNumberOfEmployeesFromEstablishment(establishment);
        if (numberOfEmployees) {
            draft.numberOfEmployees = {
                slice: {
                    label: '',
                    value: numberOfEmployees,
                },
            };
        }

        const yearsOfExperience = getYearsOfExperienceFromEstablishment(establishment);
        if (yearsOfExperience) {
            draft.yearsOfExperience = yearsOfExperience;
        }

        const homeAddress = fromEstablishment(establishment);
        if (homeAddress) {
            draft.homeAddress = homeAddress;
        }
    });
}

export function handleSelectEconomicSector(
    state: JourneyData,
    action: Extract<JourneyAction, { type: 'SELECT_ECONOMIC_SECTOR' }>,
): JourneyData {
    return produce(state, (draft) => {
        draft.economicSector = action.economicSector;
        draft.isEconomicSectorValid = isAcceptedValidityStatus(action.validity);
        if (state.recommandation?.value &&
            action.economicSector.sector?.value !== state.economicSector.sector?.value) {
            draft.catalogRecommandation.selectedProducts = undefined;
            draft.isCatalogRecommandationValid = false;
        }
    });
}

export function handleSetConfirmation(
    state: JourneyData,
    action: Extract<JourneyAction, { type: 'SET_CONFIRMATION' }>,
): JourneyData {
    return produce(state, (draft) => {
        draft.folder = {
            id: state.folder.id,
            ...state.folder as Draft<Dossier>,
            ...action.folder as Draft<Dossier>,
        };
    });
}

export function handleSetDefaultAdvisor(
    state: JourneyData,
    action: Extract<JourneyAction, { type: 'SET_DEFAULT_ADVISOR' }>,
): JourneyData {
    return produce(state, (draft) => {
        draft.folder = action.folder as Draft<Dossier>;

        const advisor: Conseiller | undefined = selectDefaultAdvisor(action.folder);
        if (advisor) {
            draft.selectedAdvisor = advisor as Draft<Conseiller>;
        }
    });
}

export function handleSetRecommandations(
    state: JourneyData,
    action: Extract<JourneyAction, { type: 'SET_RECOMMANDATIONS' }>,
): JourneyData {
    return produce(state, (draft) => {
        draft.catalogRecommandation = {
            recommandationProducts: action.products,
        };
        draft.isCatalogRecommandationValid = true;
    });
}

export const journeyReducer: JourneyReducer =
    (state: JourneyData, action: JourneyAction): JourneyData => {
        switch (action.type) {
            case 'INITIALIZE':
                return handleInitialize(state, action);
            case 'RESET_COMPANY':
                return handleResetCompany(state, action);
            case 'SELECT_ADVISOR':
                return handleSelectAdvisor(state, action);
            case 'SELECT_COMPANY':
                return handleSelectCompany(state, action);
            case 'SELECT_ECONOMIC_SECTOR':
                return handleSelectEconomicSector(state, action);
            case 'SET_CONFIRMATION':
                return handleSetConfirmation(state, action);
            case 'SET_DEFAULT_ADVISOR':
                return handleSetDefaultAdvisor(state, action);
            case 'SET_RECOMMANDATIONS':
                return handleSetRecommandations(state, action);
            case 'REQUEST_ERROR':
                return handleRequestsErrors(state, action);
            default:
                return mergeWith({}, state, action.payload, (_, srcValue) => {
                    if (Array.isArray(srcValue)) {
                        // Replaces array properties
                        return srcValue;
                    }
                    return undefined;
                });
        }
    };
